import React from "react";
import { view } from "../../lib/vvm";
import Button, { ButtonDesign } from "../../components/Button/Button";
import FieldSet from "../../components/FieldSet/FieldSet";
import Input from "../../components/Input/Input";
import { AppleLoginWidget } from "../../components/AppleLoginWidget/AppleLoginWidget";
import { GoogleLoginWidget } from "../../components/GoogleLoginWidget/GoogleLoginWidget";
import { TelegramLoginWidget } from "../../components/TelegramLoginWidget/TelegramLoginWidget";
import korobochka from "../../Boxy.png";
import { LoginViewModel } from "./LoginViewModel";
import styles from "./Login.module.css";

export const Login: React.FC = view(LoginViewModel)(({
  viewModel: {
    username,
    password,
    title,
    subtitle,
    submitLabel,
    switchModeLabel,
    isRegisterMode,
    isTelegramMiniApp,
    telegramBotUsername,
    googleClientId,
    appleClientId,
    appleRedirectUri,
    isSubmitting,
    isTelegramSubmitting,
    isGoogleSubmitting,
    isAppleSubmitting,
    canSubmit,
    canTelegramSubmit,
    canUseTelegramWidget,
    canUseGoogleLogin,
    canUseAppleLogin,
    error,
    notice,
    onUsernameChange,
    onPasswordChange,
    toggleMode,
    submit,
    submitTelegram,
    submitTelegramFromWidget,
    submitGoogleIdToken,
    submitAppleIdToken,
    onSocialAuthError,
  },
}) => {
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  return (
    <div className={styles.root}>
      <form className={styles.card} onSubmit={onSubmit}>
        <div className={styles.logoWrap}>
          <img
            className={styles.logo}
            src={korobochka}
            alt="Логотип Коробочка"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </div>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>

        <FieldSet title={"логин или email"}>
          <Input
            value={username}
            onChange={onUsernameChange}
            placeholder="admin или you@example.com"
            autoFocus
            autoComplete="username"
          />
        </FieldSet>

        <FieldSet title={"пароль"}>
          <Input
            type="password"
            value={password}
            onChange={onPasswordChange}
            placeholder="12345"
            autoComplete={"current-password"}
          />
        </FieldSet>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.notice}>{notice}</p>}

        <div className={styles.actions}>
          <Button
            design={ButtonDesign.primary}
            type="submit"
            isLoading={isSubmitting}
            disabled={!canSubmit}
          >
            {submitLabel}
          </Button>
          <Button
            design={ButtonDesign.ghost}
            type="button"
            onClick={toggleMode}
            disabled={isSubmitting}
          >
            {switchModeLabel}
          </Button>
        </div>

        {!isRegisterMode && (
          <div className={styles.socialBlock}>
            {canUseGoogleLogin ? (
              <GoogleLoginWidget
                className={styles.googleWidget}
                clientId={googleClientId}
                onCredential={(token) => void submitGoogleIdToken(token)}
                onError={onSocialAuthError}
                disabled={isGoogleSubmitting || isAppleSubmitting}
                text={"continue_with"}
                width={320}
              />
            ) : null}

            {canUseAppleLogin ? (
              <AppleLoginWidget
                className={styles.appleWidget}
                clientId={appleClientId}
                redirectUri={appleRedirectUri}
                onIdToken={(token) => void submitAppleIdToken(token)}
                onError={onSocialAuthError}
                disabled={isAppleSubmitting || isGoogleSubmitting}
                label={isAppleSubmitting ? "Apple..." : "Sign in with Apple"}
              />
            ) : null}

            {isTelegramMiniApp ? (
              <>
                <Button
                  design={ButtonDesign.secondary}
                  type="button"
                  className={styles.telegramButton}
                  onClick={() => void submitTelegram()}
                  disabled={!canTelegramSubmit}
                  isLoading={isTelegramSubmitting}
                >
                  войти через Telegram
                </Button>
                {!canTelegramSubmit && (
                  <p className={styles.telegramHint}>
                    Не удалось получить `initData` из Telegram Mini App.
                  </p>
                )}
              </>
            ) : (
              <>
                {canUseTelegramWidget ? (
                  <TelegramLoginWidget
                    className={styles.telegramWidget}
                    botUsername={telegramBotUsername}
                    onAuth={(payload) => void submitTelegramFromWidget(payload)}
                    size={"large"}
                    cornerRadius={8}
                    requestAccess={"write"}
                  />
                ) : null}
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
});
